/**
 * Hooks de datos de tareas (TanStack Query).
 *
 * TanStack Query guarda en memoria lo que trae de la API y lo mantiene al día: vuelve a pedirlo
 * cada 30 segundos y al volver a la pestaña, así el tablero refleja lo que hacen otras personas.
 *
 * Claves de la caché (para invalidar con precisión):
 *   ['tareas', <consulta>]  cada lista con sus filtros
 *   ['tarea', <id>]         el detalle de una tarea
 *   ['personas']            las personas
 */
import {
  keepPreviousData,
  useMutation,
  useMutationState,
  useQuery,
  useQueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import {
  ETIQUETA_ESTADO,
  formatearNumero,
  type DescarteEntrada,
  type Estado,
  type ListaTareas,
  type ResultadoTarea,
  type Tarea,
  type TareaDetalle,
} from '@cdtec-backlog/contrato';
import { useAvisos } from '../../componentes/Avisos';
import { META_CARGA_DE_PAGINA } from '../../componentes/BarraCargaPagina';
import { mensajeDeError } from '../../lib/api';
import {
  actualizarTarea,
  agregarBloqueo,
  agregarNota,
  aConsulta,
  consultaBuscador,
  consultaParecidas,
  consultaSubtareas,
  crearTarea,
  eliminarTarea,
  listarPersonas,
  listarTareas,
  MAX_PARECIDAS,
  moverTarea,
  obtenerTarea,
  quitarBloqueo,
  type FiltrosUI,
} from './api';

const REFRESCO_MS = 30_000;

/**
 * `activo: false` no pide nada (p. ej. el aviso de cerradas cuando el buscador está vacío).
 * `principal`: la lista de la página. Al cambiar los filtros se sigue viendo el resultado anterior
 * mientras llega el nuevo (`keepPreviousData`): solo la primera carga muestra esqueletos. Además
 * enciende la línea de carga de la página (`BarraCargaPagina`).
 */
export function useTareas(
  filtros: FiltrosUI,
  opciones: { activo?: boolean; principal?: boolean } = {},
) {
  const consulta = aConsulta(filtros);
  return useQuery({
    queryKey: ['tareas', consulta],
    queryFn: () => listarTareas(consulta),
    refetchInterval: REFRESCO_MS,
    enabled: opciones.activo ?? true,
    ...(opciones.principal
      ? { placeholderData: keepPreviousData, meta: META_CARGA_DE_PAGINA }
      : {}),
  });
}

/** Mínimo de letras del título para buscar tareas parecidas. */
export const MINIMO_PARECIDAS = 3;

const palabrasDe = (texto: string) =>
  texto
    .toLocaleLowerCase('es')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .split(/[^a-z0-9ñ]+/)
    .filter((palabra) => palabra.length >= MINIMO_PARECIDAS);

/**
 * Ordena las candidatas por cuántas palabras del título comparten (y, a igual puntaje, las más
 * recientes primero) y deja las `MAX_PARECIDAS` mejores. Función pura: se prueba sin red.
 */
export function ordenarParecidas(titulo: string, candidatas: Tarea[]): Tarea[] {
  const buscadas = palabrasDe(titulo);
  const puntaje = (tarea: Tarea) => {
    const propias = palabrasDe(tarea.titulo);
    return buscadas.filter((b) => propias.some((p) => p.startsWith(b) || b.startsWith(p))).length;
  };
  return candidatas
    .map((tarea) => ({ tarea, puntos: puntaje(tarea) }))
    .filter(({ puntos }) => puntos > 0)
    .sort(
      (a, b) => b.puntos - a.puntos || b.tarea.actualizadoEn.localeCompare(a.tarea.actualizadoEn),
    )
    .slice(0, MAX_PARECIDAS)
    .map(({ tarea }) => tarea);
}

/**
 * «Tareas parecidas» al escribir el título de una tarea nueva: abiertas y cerradas, usando la búsqueda
 * de la API. Quien llama pasa el título ya con retardo (para no pedir en cada tecla).
 */
export function useTareasParecidas(titulo: string) {
  const limpio = titulo.trim();
  const consulta = consultaParecidas(limpio);
  return useQuery({
    queryKey: ['tareas', consulta],
    queryFn: () => listarTareas(consulta),
    enabled: limpio.length >= MINIMO_PARECIDAS,
    select: (lista) => ordenarParecidas(limpio, lista.items),
    staleTime: 15_000,
  });
}

export function useTareaDetalle(id: number | null) {
  return useQuery({
    queryKey: ['tarea', id],
    queryFn: () => obtenerTarea(id as number),
    enabled: id !== null,
  });
}

export function usePersonas() {
  return useQuery({
    queryKey: ['personas'],
    queryFn: listarPersonas,
    staleTime: 60_000,
    meta: META_CARGA_DE_PAGINA,
  });
}

/**
 * Tras cualquier cambio se vuelven a pedir las listas y el detalle de las tareas tocadas. Una subtarea que
 * cambia (estado, madre) también cambia a su madre (avance, lista de subtareas, historial): quien llama pasa
 * los ids de todas las afectadas (`undefined` y `null` se ignoran).
 */
function useRefrescarTareas() {
  const cliente = useQueryClient();
  return (...ids: (number | null | undefined)[]) => {
    void cliente.invalidateQueries({ queryKey: ['tareas'] });
    for (const id of new Set(ids)) {
      if (typeof id === 'number') void cliente.invalidateQueries({ queryKey: ['tarea', id] });
    }
  };
}

/** Muestra las advertencias (no son errores) que devuelve la API, como el exceso de tareas en curso. */
function useAvisarAdvertencias() {
  const { mostrar } = useAvisos();
  return (resultado: ResultadoTarea) => {
    for (const advertencia of resultado.advertencias) mostrar(advertencia, 'advertencia');
  };
}

export function useCrearTarea() {
  const refrescar = useRefrescarTareas();
  const advertir = useAvisarAdvertencias();
  const { mostrar } = useAvisos();
  return useMutation({
    mutationFn: crearTarea,
    onSuccess: (resultado) => {
      // Una subtarea nueva cambia a su madre (avance y lista).
      refrescar(resultado.tarea.padre?.id);
      mostrar(`Tarea CDT-${resultado.tarea.numero} creada.`);
      advertir(resultado);
    },
    onError: (error) => mostrar(mensajeDeError(error), 'error'),
  });
}

/**
 * `errorEnLinea`: el error lo muestra quien llama junto al campo (con `onError`), así que no se
 * repite como aviso flotante; tampoco el «Cambios guardados.», porque el campo ya muestra «Guardado».
 */
export function useActualizarTarea(id: number, opciones: { errorEnLinea?: boolean } = {}) {
  const refrescar = useRefrescarTareas();
  const advertir = useAvisarAdvertencias();
  const { mostrar } = useAvisos();
  return useMutation({
    mutationFn: (cambios: Parameters<typeof actualizarTarea>[1]) => actualizarTarea(id, cambios),
    onSuccess: (resultado) => {
      refrescar(id, resultado.tarea.padre?.id);
      if (!opciones.errorEnLinea) mostrar('Cambios guardados.');
      advertir(resultado);
    },
    onError: (error) => {
      if (!opciones.errorEnLinea) mostrar(mensajeDeError(error), 'error');
    },
  });
}

/** Clave de las mutaciones de mover: el tablero la usa para saber qué tarjetas esperan al servidor. */
const CLAVE_MOVER = ['mover-tarea'] as const;

interface Movimiento {
  id: number;
  estado: Estado;
  descarte?: DescarteEntrada;
}

/** Ids de las tareas que se están moviendo (ya cambiadas en pantalla, esperando la confirmación). */
export function useTareasMoviendose(): ReadonlySet<number> {
  const ids = useMutationState({
    filters: { mutationKey: CLAVE_MOVER, status: 'pending' },
    select: (mutacion) => (mutacion.state.variables as Movimiento | undefined)?.id,
  });
  return new Set(ids.filter((id): id is number => id !== undefined));
}

/** «CDT-4 movida a «En curso».», o «descartada» (descartar no es un movimiento del flujo). */
function textoMovida(numero: number, estado: Estado): string {
  const id = formatearNumero(numero);
  return estado === 'DESCARTADA'
    ? `${id} descartada.`
    : `${id} movida a «${ETIQUETA_ESTADO[estado]}».`;
}

/**
 * Mover una tarea de columna es la acción más frecuente, así que se aplica de inmediato en
 * pantalla (actualización optimista) y se guarda en segundo plano; mientras tanto la tarjeta se ve
 * atenuada con un giro (`useTareasMoviendose`). Si el servidor la rechaza, la tarjeta vuelve a su
 * columna anterior y un aviso explica por qué, con «Reintentar».
 */
export function useMoverTarea(opciones: { errorEnLinea?: boolean } = {}) {
  const cliente = useQueryClient();
  const refrescar = useRefrescarTareas();
  const advertir = useAvisarAdvertencias();
  const { mostrar } = useAvisos();

  const mutacion = useMutation({
    mutationKey: CLAVE_MOVER,
    mutationFn: ({ id, estado, descarte }: Movimiento) => moverTarea(id, estado, descarte),
    onMutate: async ({ id, estado }) => {
      await cliente.cancelQueries({ queryKey: ['tareas'] });
      const anteriores: [QueryKey, ListaTareas | undefined][] = cliente.getQueriesData<ListaTareas>(
        {
          queryKey: ['tareas'],
        },
      );
      // El número (CDT-4) para el aviso de error: la respuesta del servidor no llega si falla.
      const numero = anteriores
        .flatMap(([, lista]) => lista?.items ?? [])
        .find((tarea) => tarea.id === id)?.numero;
      cliente.setQueriesData<ListaTareas>({ queryKey: ['tareas'] }, (lista) =>
        lista
          ? {
              ...lista,
              items: lista.items.map((tarea) => (tarea.id === id ? { ...tarea, estado } : tarea)),
            }
          : lista,
      );
      return { anteriores, numero };
    },
    onError: (error, variables, contexto) => {
      contexto?.anteriores.forEach(([clave, datos]) => cliente.setQueryData(clave, datos));
      if (opciones.errorEnLinea) return;
      const cual = contexto?.numero ? ` ${formatearNumero(contexto.numero)}` : ' la tarea';
      mostrar(`No se pudo mover${cual}: ${mensajeDeError(error)}`, 'error', {
        accion: { texto: 'Reintentar', alPulsar: () => mutacion.mutate(variables) },
      });
    },
    onSuccess: (resultado, variables) => {
      // Mover una subtarea cambia el avance de su madre (su ficha puede estar abierta). Cerrar o reabrir una tarea
      // también cambia a las que la esperan (quedan libres o vuelven a estar bloqueadas) y el estado que muestran
      // las que ella espera en «Bloquea a».
      const { tarea } = resultado;
      refrescar(
        tarea.padre?.id,
        ...tarea.bloqueaA.map((t) => t.id),
        ...tarea.bloqueadaPor.map((t) => t.id),
      );
      mostrar(textoMovida(tarea.numero, variables.estado));
      advertir(resultado);
    },
    onSettled: (_datos, _error, variables) => refrescar(variables.id),
  });
  return mutacion;
}

export function useAgregarNota(id: number) {
  const refrescar = useRefrescarTareas();
  const { mostrar } = useAvisos();
  return useMutation({
    mutationFn: (texto: string) => agregarNota(id, texto),
    onSuccess: () => refrescar(id),
    onError: (error) => mostrar(mensajeDeError(error), 'error'),
  });
}

export function useEliminarTarea() {
  const cliente = useQueryClient();
  const refrescar = useRefrescarTareas();
  const { mostrar } = useAvisos();
  return useMutation({
    mutationFn: eliminarTarea,
    onSuccess: (_resultado, id) => {
      // Eliminar una tarea cambia a las relacionadas: su madre (pierde una subtarea), sus subtareas (quedan
      // sueltas), las que la esperaban (ya no la esperan: pueden quedar libres) y las que esperaba (pierden una
      // fila en «Bloquea a»). Se sacan del detalle en caché (se elimina desde su ficha).
      const detalle = cliente.getQueryData<TareaDetalle>(['tarea', id]);
      if (detalle) {
        refrescar(
          detalle.padre?.id,
          ...detalle.subtareas.map((t) => t.id),
          ...detalle.bloqueaA.map((t) => t.id),
          ...detalle.bloqueadaPor.map((t) => t.id),
        );
      } else {
        // Sin el detalle no se sabe cuáles: se marcan vencidos todos (solo se vuelve a pedir el que esté a la vista).
        refrescar();
        void cliente.invalidateQueries({ queryKey: ['tarea'] });
      }
      mostrar('Tarea eliminada.');
    },
    onError: (error) => mostrar(mensajeDeError(error), 'error'),
  });
}

/* ------------------------------ Subtareas ------------------------------- */

/**
 * Subtareas de una madre (abiertas y cerradas) como tareas completas. Va bajo `['tareas', …]`, así cualquier
 * cambio que refresca las listas también la refresca. `null` no pide nada.
 */
export function useSubtareas(madreId: number | null) {
  const consulta = madreId === null ? '' : consultaSubtareas(madreId);
  return useQuery({
    queryKey: ['tareas', consulta],
    queryFn: () => listarTareas(consulta),
    enabled: madreId !== null,
    select: (lista) => lista.items,
  });
}

/** Resultados de `BuscadorTarea`. Quien llama pasa el texto ya con retardo; vacío no pide nada. */
export function useBuscarTareas(texto: string) {
  const limpio = texto.trim();
  const consulta = consultaBuscador(limpio);
  return useQuery({
    queryKey: ['tareas', consulta],
    queryFn: () => listarTareas(consulta),
    enabled: limpio.length > 0,
    select: (lista) => lista.items,
    staleTime: 15_000,
  });
}

/** Referencia mínima a una tarea para los avisos («CDT-12»). */
interface RefTarea {
  id: number;
  numero: number;
}

export interface CambioDeMadre {
  /** La subtarea: el PATCH va a ella (`padreId` es parte de SU requerimiento). */
  hija: RefTarea;
  /** Madre nueva, o `null` para soltarla. */
  madre: RefTarea | null;
  /** Madre que tenía antes (para refrescarla y para el aviso al soltar). */
  anterior: RefTarea | null;
}

/**
 * Elegir, cambiar o quitar la tarea madre de una subtarea (PATCH `{ padreId }` a la hija). Lo usan el bloque
 * «Tarea madre» (desde la hija) y la sección «Subtareas» (desde la madre: «Vincular existente» y quitar).
 * Refresca las listas y el detalle de la hija, de la madre nueva y de la anterior.
 */
export function useCambiarMadre() {
  const refrescar = useRefrescarTareas();
  const advertir = useAvisarAdvertencias();
  const { mostrar } = useAvisos();
  return useMutation({
    mutationFn: ({ hija, madre }: CambioDeMadre) =>
      actualizarTarea(hija.id, { padreId: madre?.id ?? null }),
    onSuccess: (resultado, { hija, madre, anterior }) => {
      refrescar(hija.id, madre?.id, anterior?.id);
      const idHija = formatearNumero(hija.numero);
      if (madre) mostrar(`${idHija} ahora es subtarea de ${formatearNumero(madre.numero)}.`);
      else if (anterior)
        mostrar(`${idHija} ya no es subtarea de ${formatearNumero(anterior.numero)}.`);
      advertir(resultado);
    },
    onError: (error) => mostrar(mensajeDeError(error), 'error'),
  });
}

/* ------------------------------- Bloqueos ------------------------------- */

export interface CambioDeBloqueo {
  /** La tarea que espera: el POST/DELETE va a ella (`/tareas/:id/bloqueos`). */
  bloqueada: RefTarea;
  /** La tarea a la que espera. */
  bloqueante: RefTarea;
}

/**
 * Agregar un bloqueo («`bloqueada` bloqueada por `bloqueante`», ADR-0018). Es colaboración: cualquiera con
 * escritura. Refresca las listas y el detalle de las DOS tareas (la bloqueante gana una fila en «Bloquea a» y
 * un evento en su historial). Los errores de la API (ciclo, ya existe, madre e hija) van al aviso flotante.
 */
export function useAgregarBloqueo() {
  const refrescar = useRefrescarTareas();
  const advertir = useAvisarAdvertencias();
  const { mostrar } = useAvisos();
  return useMutation({
    mutationFn: ({ bloqueada, bloqueante }: CambioDeBloqueo) =>
      agregarBloqueo(bloqueada.id, bloqueante.id),
    onSuccess: (resultado, { bloqueada, bloqueante }) => {
      refrescar(bloqueada.id, bloqueante.id);
      mostrar(
        `${formatearNumero(bloqueada.numero)} quedó bloqueada por ${formatearNumero(bloqueante.numero)}.`,
      );
      advertir(resultado);
    },
    onError: (error) => mostrar(mensajeDeError(error), 'error'),
  });
}

/** Quitar un bloqueo. Refresca las listas y el detalle de las dos tareas, como al agregarlo. */
export function useQuitarBloqueo() {
  const refrescar = useRefrescarTareas();
  const advertir = useAvisarAdvertencias();
  const { mostrar } = useAvisos();
  return useMutation({
    mutationFn: ({ bloqueada, bloqueante }: CambioDeBloqueo) =>
      quitarBloqueo(bloqueada.id, bloqueante.id),
    onSuccess: (resultado, { bloqueada, bloqueante }) => {
      refrescar(bloqueada.id, bloqueante.id);
      mostrar(
        `${formatearNumero(bloqueada.numero)} ya no está bloqueada por ${formatearNumero(bloqueante.numero)}.`,
      );
      advertir(resultado);
    },
    onError: (error) => mostrar(mensajeDeError(error), 'error'),
  });
}
